/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import { Box, Button, CircularProgress, InputAdornment, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { InputCustom } from "../../../styles/styledComponents";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PromoCode } from "../../../components";
import { epochToDateString } from "../../../utils/unix-timestamp";
import { paletteColors } from "../../../styles/muiTheme";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useVerifyPromoCode } from "../../../services/promo-code-service/promo-code.service";
import { userActivityLogger } from "../../../utils/mixpanel";
import { useBookingAppointmentTypeStore } from "../../../hooks";
import { ASSIGNED_BOOKING_TYPE } from "../../../config/params";

type IPromoCodeApplyProps = {}

type IPromoCodes = {
  _id: string;
  promoCode: string;
  description: string;
}[];

const PromoCodeApply = (props: IPromoCodeApplyProps) => {
  const { appointmentForm, selectedPets, setPromoCodeData, pastAppointments } = useAppointmentStore();
  const { promoCodeResponse, loading, getVerifyPromoCode } = useVerifyPromoCode();
  const { appointmentTypeIndicator, bookingAppointmentType } = useBookingAppointmentTypeStore();
  const [promoSuccessMsg, setPromoSuccessMsg] = useState<string | null>(null);
  const [promoCodeState, setPromoCodeState] = useState({
    promoCodeValue: "",
    isValidPromoCode: false,
    hasApplyCodeClicked: false,
    promoCodeErrorMsg: "",
  });

  const [appliedPromoCodes, setAppliedPromoCodes] = useState<IPromoCodes>([]);

  useEffect(()=>{
    // to reset the promocode state to default
    setPromoCodeData([]);
  }, []);

  const commonEvents = {
    pet_name: selectedPets[0]?.name,
    date: epochToDateString(Number(appointmentForm?.time)),
    time_slot: appointmentForm?.selectedSlotLabel,
    user_type: pastAppointments.length > 0 ? "existing user" : "new user",
  };

  const getAppointmentTypeText = () =>{
    switch (appointmentTypeIndicator) {
    case ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING:
      return "drop_off";
    case ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING:
      return "express_visit";
    default:
      return "in_room_visit";
    }
  };

  const handleApplyPromoCode = () => {
    if (!promoCodeState.promoCodeValue) {
      setPromoCodeState((prevState)=>({ ...prevState, promoCodeErrorMsg: "Please enter a promo code" }));

      return;
    }

    // Mixpanel event

    userActivityLogger(MIXPANEL_EVENTS.PROMOCODE_VALIDATION_TRIGGERED, {
      ...commonEvents,
      promocode: promoCodeState.promoCodeValue,
      appt_type: getAppointmentTypeText(),
    });

    setPromoCodeState((prevState)=>({ ...prevState, hasApplyCodeClicked: true }));

    // payload for verifyPromoCode
    const verifyPromoInput = {
      bookingTime: Number(appointmentForm?.time),
      previousAppliedPromos: appliedPromoCodes.map((code)=>code.promoCode),
      pets: selectedPets.map((pet)=>({
        petId: pet.id,
        promoCode: promoCodeState.promoCodeValue,
      })),
    };

    const isAlreadyApplied = appliedPromoCodes.some((code)=>code.promoCode === promoCodeState.promoCodeValue);

    if (isAlreadyApplied) {
      setPromoCodeState((prevState)=>({ ...prevState, isValidPromoCode: false,
        promoCodeErrorMsg: "Promo code already applied" }));

      return;
    } else {
      getVerifyPromoCode(verifyPromoInput);
    }
  };

  useEffect(()=>{
    if (promoCodeResponse && !loading) {
      if (promoCodeResponse.success && promoCodeResponse.basePromoCode?.length) {
        setAppliedPromoCodes((prevCodes) => {
          const isAlreadyApplied = prevCodes.some(
            (code) => code._id === promoCodeResponse?.basePromoCode?.[0]?._id,
          );

          // check for duplicate promo code

          if (!isAlreadyApplied) {
            setPromoSuccessMsg(promoCodeResponse?.messages?.[0]);

            // Mixpanel event

            userActivityLogger(MIXPANEL_EVENTS.PROMOCODE_VALIDATION_SUCCESS, {
              ...commonEvents,
              promocode: promoCodeState.promoCodeValue,
              appt_type: getAppointmentTypeText(),
            });

            return [
              ...prevCodes,
              {
                _id: promoCodeResponse?.basePromoCode?.[0]?._id,
                promoCode: promoCodeResponse?.basePromoCode?.[0]?.promoCode,
                description: promoCodeResponse?.basePromoCode?.[0]?.description,
              },
            ];
          }

          return prevCodes;
        });
      }
      setPromoCodeState((prevState)=>({ ...prevState, isValidPromoCode: true, promoCodeValue: "", promoCodeErrorMsg: "" }));
      if (!promoCodeResponse.success) {
        userActivityLogger(MIXPANEL_EVENTS.PROMOCODE_VALIDATION_FAILED, {
          ...commonEvents,
          failure_reason: promoCodeResponse?.messages[0],
          promocode: promoCodeState.promoCodeValue,
          appt_type: getAppointmentTypeText(),
        });

        setPromoCodeState((prevState)=>({ ...prevState, isValidPromoCode: false,
          promoCodeErrorMsg: promoCodeResponse?.messages[0] }));
      }
    }
  }, [promoCodeResponse]);

  useEffect(()=>{
    if (appliedPromoCodes.length > 0) {
      setPromoCodeData(appliedPromoCodes.map((code)=>code.promoCode));
    }
  }, [appliedPromoCodes]);

  // remove promo code
  const handleRemoveCode = (id: string)=>{
    const filteredCodes = appliedPromoCodes.filter((code) => code._id !== id);

    if (filteredCodes.length!==appliedPromoCodes.length) {
      // remove event for MixPanel
      userActivityLogger(MIXPANEL_EVENTS.PROMOCODE_REMOVED, {
        ...commonEvents,
        promocode: appliedPromoCodes.find((code)=>code._id === id)?.promoCode,
        appt_type: getAppointmentTypeText(),
      });

      setAppliedPromoCodes(filteredCodes);
      // update the promo code data for redux state as well
      setPromoCodeData(filteredCodes.map((code)=>code.promoCode));
    };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const alphanumericRegex = /^[a-zA-Z0-9]*$/;

    if (alphanumericRegex.test(e.target.value) === true) {
      if (e.target.value.length === 0) {
        setPromoCodeState((prevState)=>({ ...prevState, isValidPromoCode: false, promoCodeErrorMsg: "", hasApplyCodeClicked: false, promoCodeValue: "" }));
      } else if (e.target.value.length <=20) {
        setPromoCodeState((prevState)=>({ ...prevState, promoCodeValue: e.target.value?.toUpperCase() }));
      }
    }
  };

  return (
    <Box sx={{
      "& .MuiFormControl-root": {
        marginBottom: "0rem !important",
      },
    }}>
      <Typography variant="body1" className="!font-bold" gutterBottom>
           Promo Code
      </Typography>

      <Box className={`p-3 flex flex-col border border-taupe-50 bg-white items-start rounded-lg
        
        ${promoCodeState.isValidPromoCode ? "gap-4" : "gap-2"}`}>

        <InputCustom variant="outlined" value={promoCodeState.promoCodeValue}
          onChange={(e) =>handleInputChange(e)}
          onFocus={()=>{ // reset error message
            if (promoCodeState.hasApplyCodeClicked && !promoCodeState.isValidPromoCode) {
              setPromoCodeState((prevState)=>({ ...prevState, hasApplyCodeClicked: false, promoCodeErrorMsg: "" }));
            }
          }}
          fullWidth placeholder={"Enter Promo Code"}
          sx={{ "& .MuiInputBase-root": { borderRadius: "0.5rem", paddingRight: "0.5rem",
            height: "3rem",
            borderColor: (!promoCodeState.isValidPromoCode && promoCodeState.hasApplyCodeClicked && !loading) ||
            promoCodeState.promoCodeErrorMsg ?
              paletteColors.error.main : paletteColors.taupe.dark,
          }, "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: promoCodeState.promoCodeErrorMsg ? "transparent": paletteColors.primary.main,
          },

          }}
          InputProps={{ endAdornment: (
            <InputAdornment position="end" sx={{ "&.MuiInputAdornment-root > button": {
              padding: "10px 12px !important",
            } }}>
              <Button variant="contained" onClick={handleApplyPromoCode}
                disabled={loading || promoCodeState.promoCodeValue.length === 0}
                size="small" sx={{ fontSize: "12px", minWidth: loading? "80px": undefined }}>{loading ?
                  <CircularProgress color="inherit" size={20} /> :
                  "Apply Code"}</Button>
            </InputAdornment>
          ),
          }}/>

        {!promoCodeState.isValidPromoCode && promoCodeState.promoCodeErrorMsg && !loading? (
          <Typography variant="body1" color={paletteColors.error.main}>
            {promoCodeState.promoCodeErrorMsg}</Typography>
        ) : null }

        <Box className="flex flex-col gap-4">

          {(appliedPromoCodes.length > 0) ? (
            <>
              {appliedPromoCodes.reverse().map((code)=>(
                <PromoCode key={code._id} promoCode={code}
                  handleRemoveCode={handleRemoveCode}/>),
              )}
              {
                promoSuccessMsg &&
                <Typography variant="body1"><strong>✓ Promo Applied:</strong> {promoSuccessMsg}</Typography>}
            </>
          ): null
          }
        </Box>


      </Box>
    </Box>
  );
};

export default PromoCodeApply;
